/**
 * Sub-tasks API client (Milestone 4-B, spec §3.9).
 *
 * Wraps the gateway REST surface in apps/chaos-gateway/src/sub-tasks.ts:
 *   GET    /api/cards/:cardId/sub-tasks
 *   POST   /api/cards/:cardId/sub-tasks
 *   PATCH  /api/sub-tasks/:id
 *   DELETE /api/sub-tasks/:id
 *   GET    /api/sub-tasks/:id/comments
 *   POST   /api/sub-tasks/:id/comments
 *   PATCH  /api/sub-task-comments/:id
 *   DELETE /api/sub-task-comments/:id
 */
import { apiJson } from './client';

export type SubTaskStatus = 'open' | 'in_progress' | 'done' | 'blocked' | 'cancelled';
export type AssignedKind = 'user' | 'role';
export type ActorKind = 'user' | 'role';

export interface SubTask {
  id: string;
  parentCardId: string;
  orgId: string;
  title: string;
  description: string | null;
  assignedToKind: AssignedKind | null;
  assignedToUserId: string | null;
  assignedToRoleId: string | null;
  status: SubTaskStatus;
  artifactRefs: unknown;
  dueAt: string | null;
  createdByKind: ActorKind | null;
  createdByUserId: string | null;
  createdByRoleId: string | null;
  completedAt: string | null;
  completedByKind: ActorKind | null;
  completedByUserId: string | null;
  completedByRoleId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SubTaskComment {
  id: string;
  subTaskId: string;
  authorKind: ActorKind;
  authorUserId: string | null;
  authorRoleId: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
  edited: boolean;
}

export interface CreateSubTaskInput {
  title: string;
  description?: string | null;
  assignedToKind?: AssignedKind | null;
  assignedToUserId?: string | null;
  assignedToRoleId?: string | null;
  status?: SubTaskStatus;
  artifactRefs?: unknown;
  dueAt?: string | null;
}

export interface UpdateSubTaskInput {
  title?: string;
  description?: string | null;
  assignedToKind?: AssignedKind | null;
  assignedToUserId?: string | null;
  assignedToRoleId?: string | null;
  status?: SubTaskStatus;
  artifactRefs?: unknown;
  dueAt?: string | null;
}

interface SubTasksListResponse {
  subTasks: SubTask[];
}
interface SubTaskResponse {
  subTask: SubTask;
}
interface CommentsListResponse {
  comments: SubTaskComment[];
}
interface CommentResponse {
  comment: SubTaskComment;
}

export const subTasksApi = {
  list(cardId: string): Promise<SubTask[]> {
    return apiJson<SubTasksListResponse>(`/cards/${cardId}/sub-tasks`).then((r) => r.subTasks);
  },
  create(cardId: string, input: CreateSubTaskInput): Promise<SubTask> {
    return apiJson<SubTaskResponse>(`/cards/${cardId}/sub-tasks`, {
      method: 'POST',
      json: input,
    }).then((r) => r.subTask);
  },
  update(id: string, input: UpdateSubTaskInput): Promise<SubTask> {
    return apiJson<SubTaskResponse>(`/sub-tasks/${id}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.subTask);
  },
  remove(id: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/sub-tasks/${id}`, { method: 'DELETE' });
  },
  listComments(id: string): Promise<SubTaskComment[]> {
    return apiJson<CommentsListResponse>(`/sub-tasks/${id}/comments`).then((r) => r.comments);
  },
  addComment(id: string, body: string): Promise<SubTaskComment> {
    return apiJson<CommentResponse>(`/sub-tasks/${id}/comments`, {
      method: 'POST',
      json: { body },
    }).then((r) => r.comment);
  },
  updateComment(commentId: string, body: string): Promise<SubTaskComment> {
    return apiJson<CommentResponse>(`/sub-task-comments/${commentId}`, {
      method: 'PATCH',
      json: { body },
    }).then((r) => r.comment);
  },
  deleteComment(commentId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/sub-task-comments/${commentId}`, { method: 'DELETE' });
  },
};

export const subTasksKeys = {
  all: ['sub-tasks'] as const,
  byCard: (cardId: string) => [...subTasksKeys.all, 'card', cardId] as const,
  comments: (subTaskId: string) => [...subTasksKeys.all, 'comments', subTaskId] as const,
};
